import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { FaPlus, FaUser, FaEdit } from "react-icons/fa";
import { PageContainer, PageTitleBadge, DataGridTable, SearchBar, FilterSelect, FiltersContainer } from "../components";
import "../styles/common.css";
import "./EnregistrerBerger.css";

export default function EnregistrerBerger() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [bergers, setBergers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [statut, setStatut] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('bergers-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('bergers-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchBergers = async () => {
      try {
        const params = new URLSearchParams();
        if (statut !== '') {
          params.append('actif', statut === 'actif' ? 'true' : 'false');
        }
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        
        const response = await api.get("/api/bergers/", { params });
        const bergers = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setBergers(bergers);
        setLoading(false);
    } catch (err) {
        setError(t.loadingListError || "Erreur lors du chargement des bergers.");
      setLoading(false);
    }
  };
      fetchBergers();
  }, [statut, searchTerm, t]);

  // Configuration des colonnes pour DataGrid
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'prenom',
        headerName: t.firstName || 'Prénom',
        flex: 1,
        minWidth: 120,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'nom',
        headerName: t.lastName || 'Nom',
        flex: 1,
        minWidth: 120,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'nom_complet',
        headerName: t.summaryFullName || 'Nom complet',
        flex: 1,
        minWidth: 180,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'telephone',
        headerName: t.phone || 'Téléphone',
        flex: 1,
        minWidth: 140,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'cni',
        headerName: t.cni || 'CNI',
        flex: 1,
        minWidth: 120,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'adresse',
        headerName: t.address || 'Adresse',
        flex: 1,
        minWidth: 160,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'date_embauche',
        headerName: t.hireDate || 'Date d\'embauche',
        width: 150,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value, row) => {
          if (!row?.date_embauche) return "N/A";
          const date = new Date(row.date_embauche);
          return date.toLocaleDateString('fr-FR');
        },
      },
      {
        field: 'actif',
        headerName: t.active || 'Actif',
        width: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <span style={{ 
            color: params.value ? '#10b981' : '#ef4444',
            fontWeight: 600 
          }}>
            {params.value ? t.yes : t.no}
          </span>
        ),
      },
    ];

    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_BERGER);
    
    const adminColumns = canEdit
      ? [
          {
            field: "edit",
            headerName: t.actions || "Actions",
            width: 130,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
              <button 
                onClick={() => navigate(`/bergers/${params.row.id}/modifier`)}
                className="grid-action-button"
                style={{ fontSize: `${1.3 * zoomLevel}rem` }}
              >
                {t.edit || "Modifier"}
              </button>
            ),
          },
        ]
      : [];

    return [...baseColumns, ...adminColumns];
  }, [user, navigate, zoomLevel, t]);

  return (
    <PageContainer>
      <PageTitleBadge title={t.shepherdList || "Liste des bergers"} icon={<FaUser />} />
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_BERGER)) && (
        <button onClick={() => navigate("/ajouter-berger")} className="add-animal-button">
          <FaPlus />
          {t.registerShepherd || "Enregistrer un berger"}
        </button>
      )}
      {loading && <p>{t.loading}</p>}
      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="animaux-container">
          <div className="top-section">
            <SearchBar
              placeholder={t.searchByMatricule || "Rechercher par nom, prénom, CNI..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="default"
            />
          </div>
          <FiltersContainer title={t.filters || "Filtres"}>
            <FilterSelect
              name="statut"
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
              options={[
                { value: "actif", label: t.active || "Actif" },
                { value: "inactif", label: t.no || "Inactif" }
              ]}
              placeholder={t.filterByStatus || "Filtrer par statut"}
              className="filtre-statut"
              width="180px"
            />
          </FiltersContainer>
          {/* DataGrid moderne pour les bergers */}
          {bergers.length === 0 ? (
            <div className="empty-message-container">
              <p className="empty-message">
                {statut || searchTerm
                  ? "Aucun berger trouvé avec les filtres sélectionnés."
                  : t.noShepherdRegistered || "Aucun berger enregistré."}
              </p>
            </div>
          ) : (
            <DataGridTable
              rows={bergers}
              columns={columns}
              loading={loading}
              storageKey="bergers-zoom-level"
              zoomLevel={zoomLevel}
              onZoomChange={setZoomLevel}
              initialSort={{ field: 'prenom', sort: 'asc' }}
              pageSize={25}
              containerClassName="berger-grid-container"
            />
          )}
        </div>
      )}
    </PageContainer>
  );
}
